import { CSSProperties } from 'react';
import { Controller, Control, FieldValues } from 'react-hook-form';
import { Stack, OutlinedInput, InputAdornment, IconButton } from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import TextFormLabel from '../text/TextFormLabel';
import TextFormError from '../text/TextFormError';
import { useLocalization } from '../../../localization';

export interface NumberInputProps<T extends FieldValues = FieldValues> {
  name: string;
  control: Control<T>;
  label: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  style?: CSSProperties;
  readonly?: boolean;
  min?: number;
  max?: number;
  step?: number;
  allowNegative?: boolean;
  decimalPlaces?: number;
  showActionIcon?: boolean;
  maxDigits?: number;
}

export const NumberInputField = <T extends FieldValues = FieldValues>(props: NumberInputProps<T>) => {
  const { t } = useLocalization();
  const {
    name,
    control,
    label,
    required = false,
    placeholder,
    disabled = false,
    readonly = false,
    style,
    min,
    max,
    step = 1,
    allowNegative = false,
    decimalPlaces,
    showActionIcon = false,
    maxDigits = 15,
  } = props;

  const formatValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    if (decimalPlaces !== undefined) {
      return value.toFixed(decimalPlaces);
    }
    return value.toString();
  };

  const parseValue = (value: string): number | null => {
    if (value === '') return null;
    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) return null;

    if (decimalPlaces !== undefined) {
      return Number(parsedValue.toFixed(decimalPlaces));
    }
    return parsedValue;
  };

  const handleChange = (value: string, onChange: (value: number | null) => void) => {
    const digitCount = value.replace(/\D/g, '').length;
    if (digitCount > maxDigits) {
      return;
    }

    // Allow typing minus sign for negative numbers
    if (allowNegative && value === '-') {
      onChange(null);
      return;
    }

    // Allow decimal point
    if (value.endsWith('.')) {
      onChange(parseValue(value.slice(0, -1)));
      return;
    }

    const parsedValue = parseValue(value);

    if (value === '') {
      onChange(null);
      return;
    }
    if (parsedValue === null) return;

    onChange(parsedValue);
  };

  return (
    <Stack spacing={0.5}>
      <TextFormLabel label={label} required={required} />
      <Controller
        name={name as any}
        control={control}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const increment = () => {
            const newValue = (value ?? 0) + step;
            onChange(newValue);
          };

          const decrement = () => {
            const newValue = (value ?? 0) - step;
            onChange(newValue);
          };
          return (
            <>
              <OutlinedInput
                value={formatValue(value)}
                onChange={e => handleChange(e.target.value, onChange)}
                placeholder={t(placeholder ?? '')}
                type="number"
                disabled={disabled}
                error={Boolean(error)}
                endAdornment={
                  showActionIcon && (
                    <InputAdornment position="end">
                      <Stack direction="column" spacing={0}>
                        <IconButton
                          onClick={increment}
                          size="small"
                          edge="end"
                          sx={{ padding: 0, borderRadius: 0, width: 18, height: 18 }}
                          disabled={value !== null && max !== undefined && value >= max}
                        >
                          <ArrowDropUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={decrement}
                          size="small"
                          edge="end"
                          sx={{ padding: 0, borderRadius: 0, width: 18, height: 18 }}
                          disabled={value !== null && min !== undefined && value <= min}
                        >
                          <ArrowDropDownIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </InputAdornment>
                  )
                }
                inputProps={{
                  min,
                  max,
                  step,
                  readOnly: readonly,
                }}
                style={{ ...style }}
                sx={{
                  // Remove native spinner icons
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
                size="small"
              />
              <TextFormError error={error} />
            </>
          );
        }}
      />
    </Stack>
  );
};
